/**
 * Top-level XAES Analyst chat panel. Composes the message list, input
 * row, reference picker (in `ChatInput`), settings modal, history
 * dropdown, and the "Save to Captures" affordance.
 *
 * Source references:
 *   - Loveable v1 reference: `src/components/XaesAnalyst.tsx` — same
 *     external shape (`workshopId` prop, dock-in-right-column usage),
 *     re-implemented on the rebuild's primitives.
 *   - `src/features/xaes-analyst/use-chat.ts` — owns chat state.
 *   - `src/features/xaes-analyst/persistence.ts` — multi-session store.
 *
 * Props:
 *   - `workshopId`: `Workshop["id"]` for a per-workshop dashboard, or
 *     the sentinel `"MC"` for Mission Control's broader context.
 */

import { useEffect, useRef, useState } from "react";
import { History, MessageSquare, Plus, Save, Settings, Trash2 } from "lucide-react";
import wdBg from "@/assets/wd-bg.jpg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useCaptures, type UserCapture } from "@/stores/useCaptures";
import { useClaudeSettings } from "@/stores/useClaudeSettings";
import type { Workshop } from "@/content/workshops";
import { cn } from "@/lib/utils";
import type { ChatScope } from "./context-builder";
import { setProgramRoster } from "./crew-context";
import { sessionToDataUrl } from "./chat-export";
import { deterministicChatCaption, summariseChat } from "./chat-summary";
import { newSessionId, type ChatSession } from "./persistence";
import { useChat } from "./use-chat";
import { useInsightSuggestions } from "./use-insight-suggestions";
import { ChatInput } from "./components/ChatInput";
import { ChatMessages } from "./components/ChatMessages";
import { ClaudeSettingsModal } from "./components/ClaudeSettingsModal";
import { InsightSuggestions } from "./components/InsightSuggestions";

type Props = {
  workshopId: ChatScope;
  className?: string;
};

/** Workshop scope guard — the Captures store is typed to
 *  `Workshop["id"]` and doesn't have a slot for the `"MC"` sentinel.
 *  We hide / soft-disable the save affordance in that case rather than
 *  shoehorning MC chat exports into a workshop bucket. */
function isWorkshopScope(scope: ChatScope): scope is Workshop["id"] {
  return scope !== "MC";
}

/** Map the hook's status to a status-pill label + dot colour. Mirrors
 *  the legacy `.analyst-status` row in `_legacy/wd.html` line 628-632:
 *  "XAES //"  + dot + Online/Offline/Thinking/etc. */
function statusDisplay(
  configured: boolean,
  status: ReturnType<typeof useChat>["status"],
): { label: string; dotColor: string; pulse: boolean } {
  if (!configured) return { label: "Mock mode", dotColor: "var(--accent-gold)", pulse: true };
  switch (status) {
    case "thinking":
      return { label: "Thinking", dotColor: "var(--accent-gold)", pulse: true };
    case "streaming":
      return { label: "Streaming", dotColor: "var(--accent-green)", pulse: true };
    case "error":
      return { label: "Error", dotColor: "var(--accent-red)", pulse: false };
    default:
      return { label: "Online", dotColor: "var(--accent-green)", pulse: true };
  }
}

/** Render a `(ms-ago)` epoch as a short relative label using the
 *  platform `Intl.RelativeTimeFormat`. Falls back to a coarse string
 *  on environments without the API. */
function formatRelative(ts: number, now: number): string {
  const diffMs = ts - now;
  const absMs = Math.abs(diffMs);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  try {
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    if (absMs < minute) return rtf.format(Math.round(diffMs / 1000), "second");
    if (absMs < hour) return rtf.format(Math.round(diffMs / minute), "minute");
    if (absMs < day) return rtf.format(Math.round(diffMs / hour), "hour");
    if (absMs < week) return rtf.format(Math.round(diffMs / day), "day");
    return rtf.format(Math.round(diffMs / week), "week");
  } catch {
    return new Date(ts).toLocaleDateString();
  }
}

export function XaesAnalyst({ workshopId, className }: Props) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saveFlash, setSaveFlash] = useState(false);
  const auth = useAuth();
  // Prefer the stable OIDC subject for persistence so chat history
  // survives preferred_username / display-name churn across logins.
  // Claim any prior username-keyed sessions on first hydrate.
  const userId = auth.user?.subject ?? auth.user?.username ?? "anon";
  const claimFromUserId =
    auth.user?.subject && auth.user.username !== auth.user.subject
      ? auth.user.username
      : undefined;
  useEffect(() => {
    setProgramRoster(auth.users);
  }, [auth.users]);
  const chat = useChat({ workshopId, userId, claimFromUserId });
  const display = statusDisplay(chat.configured, chat.status);
  const { toast } = useToast();
  const addedBy =
    auth.user?.displayName ?? auth.user?.username ?? "anon";

  // AI-CHAT-INSIGHTS — auto-surface knowledge-register insights from the
  // conversation, committed only on an explicit Add (suggest-then-confirm).
  const insights = useInsightSuggestions({
    scope: workshopId,
    messages: chat.messages,
    status: chat.status,
    addedBy,
  });

  function handleAcceptInsight(id: string): void {
    const added = insights.accept(id);
    if (!added) return;
    const KIND_LABEL: Record<typeof added.kind, string> = {
      finding: "Finding",
      opportunity: "Opportunity",
      recommendation: "Recommendation",
      decision: "Decision",
      action: "Action",
      assumption: "Assumption",
      note: "Note",
    };
    const insightLibrary =
      added.kind === "finding" ||
      added.kind === "opportunity" ||
      added.kind === "recommendation";
    const target = added.workshopId ? ` (${added.workshopId})` : "";
    try {
      toast({
        title: insightLibrary ? "Added to Insight Library" : "Added to workshop register",
        description: `${KIND_LABEL[added.kind]}${target} — ${added.title}`,
      });
    } catch {
      // useToast is global; defensive — the tray removing the card is the
      // load-bearing acknowledgement.
      console.info("[xaes-analyst] added insight", added);
    }
  }
  // Live "now" tick for the relative timestamps in the history dropdown.
  // Cheap heartbeat — only ticks while the dropdown is open.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!historyOpen) return;
    setNow(Date.now());
    const handle = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(handle);
  }, [historyOpen]);

  const saveFlashTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (saveFlashTimerRef.current !== null) {
        window.clearTimeout(saveFlashTimerRef.current);
      }
    },
    [],
  );

  const canSaveToLocker =
    isWorkshopScope(workshopId) &&
    chat.activeSession !== null &&
    chat.activeSession.messages.some(
      (m) => !m.streaming && !m.error && m.text.trim().length > 0,
    );

  function handleSaveToLocker(): void {
    if (!canSaveToLocker || !chat.activeSession) return;
    if (!isWorkshopScope(workshopId)) return; // narrows for TS
    const session = chat.activeSession;
    const dataUrl = sessionToDataUrl(session);
    const addedAt = Date.now();
    const timestamp = new Date(addedAt).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const title = session.title || "New chat";
    const captureId = newSessionId();
    // Preview caption = "what was talked about". Seed it synchronously from
    // the user's own prompts so the card reads usefully the instant it
    // lands (and in mock mode, where there's no model), then upgrade it to
    // a one-line AI summary in the background below.
    const baseCaption = deterministicChatCaption(session);
    // Migration note: chat exports used to land in `useAssetLocker`
    // (Deliverables grid). They now live on the Captures surface so
    // user-saved artefacts share one home — see `useCaptures.ts`.
    const capture: UserCapture = {
      id: captureId,
      workshopId,
      title,
      activity: "XAES Analyst chat",
      timestamp,
      kind: "chat",
      accent: "sky",
      source: "Saved",
      caption: baseCaption,
      url: dataUrl,
      mimeType: "text/markdown",
      addedAt,
      addedBy: auth.user?.displayName ?? auth.user?.username ?? "anon",
    };
    useCaptures.getState().add(workshopId, capture);

    // AI-CHAT-SUMMARY — upgrade the caption to a one-line summary of what
    // was discussed. Best-effort + fire-and-forget: dormant when neither the
    // agent backend nor a browser Anthropic key is available (summariseChat
    // returns null), and a failure just leaves the deterministic caption.
    // The IDB-only update() keeps this caption edit off the blob-sync path.
    void summariseChat({
      session,
      scope: workshopId,
      model: useClaudeSettings.getState().model,
    }).then((summary) => {
      if (summary) {
        useCaptures.getState().update(workshopId, captureId, { caption: summary });
      }
    });

    // Visual ack: brief sky flash + toast.
    setSaveFlash(true);
    if (saveFlashTimerRef.current !== null) {
      window.clearTimeout(saveFlashTimerRef.current);
    }
    saveFlashTimerRef.current = window.setTimeout(() => {
      setSaveFlash(false);
      saveFlashTimerRef.current = null;
    }, 600);

    try {
      toast({
        title: "Saved to Captures",
        description: capture.title,
      });
    } catch {
      // useToast is global; the catch is defensive — the brief flash
      // is the load-bearing user-facing signal.
      console.info("[xaes-analyst] saved chat to Captures", capture);
    }
  }

  return (
    <>
      <div
        className={cn("panel h-full min-h-0", className)}
        aria-label="XAES Analyst chat"
      >
        {/* Panel header — legacy `_legacy/wd.html` lines 619-627:
            avatar circle (cropped wd-bg.jpg, sky-blue border + cyan
            glow) + "XAES Analyst" title + history / save / settings
            controls. */}
        <div className="panel-header">
          <div className="panel-title">
            <span
              aria-hidden
              className="flex-shrink-0 overflow-hidden rounded-full"
              style={{
                width: 26,
                height: 26,
                border: "1.5px solid var(--accent-sky)",
                boxShadow: "var(--glow-cyan)",
              }}
            >
              <img
                src={wdBg}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "60% center",
                }}
              />
            </span>
            XAES Analyst
          </div>

          {/* Header control cluster — history dropdown, save-to-locker,
              settings cog. Each is styled to track `--text-muted` so
              the cluster reads as a single chrome unit, matching the
              legacy `.panel-menu` aesthetic. */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <DropdownMenu open={historyOpen} onOpenChange={setHistoryOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Chat history"
                  className="panel-menu"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    background: "none",
                    border: 0,
                    padding: "2px 4px",
                    cursor: "pointer",
                    color: historyOpen
                      ? "var(--accent-true-blue)"
                      : "var(--text-muted)",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "var(--fs-label)",
                    fontWeight: 600,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    transition: "color 0.15s",
                  }}
                >
                  <History
                    aria-hidden
                    style={{ width: 13, height: 13 }}
                    strokeWidth={1.8}
                  />
                  History
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={6}
                // Match the ReferencePicker chrome: bg-panel + true-blue
                // 0.35 border + soft cyan glow. Override the shadcn
                // defaults so this dropdown reads as a sibling of the
                // existing chat popovers, not a generic shadcn menu.
                className="w-auto p-0"
                style={{
                  minWidth: 280,
                  maxWidth: 360,
                  background: "var(--bg-panel)",
                  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.35)",
                  borderRadius: 10,
                  overflow: "hidden",
                  boxShadow:
                    "0 0 30px rgba(var(--accent-true-blue-rgb), 0.12), 0 8px 24px rgba(0, 0, 0, 0.3)",
                }}
              >
                <HistoryList
                  sessions={chat.sessions}
                  sessionsHydrated={chat.sessionsHydrated}
                  activeId={chat.activeSessionId}
                  now={now}
                  onNew={() => {
                    chat.startNewSession();
                    setHistoryOpen(false);
                  }}
                  onPick={(id) => {
                    chat.loadSession(id);
                    setHistoryOpen(false);
                  }}
                  onDelete={(id) => {
                    void chat.deleteSession(id);
                  }}
                />
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              onClick={handleSaveToLocker}
              disabled={!canSaveToLocker}
              aria-label={
                canSaveToLocker
                  ? "Save chat to Captures"
                  : isWorkshopScope(workshopId)
                    ? "Send a message before saving"
                    : "Mission Control chats can't be saved to a workshop Captures grid"
              }
              title={
                canSaveToLocker
                  ? "Save chat to Captures"
                  : isWorkshopScope(workshopId)
                    ? "Send a message before saving"
                    : "Mission Control chats can't be saved"
              }
              className="panel-menu"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "none",
                border: 0,
                padding: "2px 4px",
                cursor: canSaveToLocker ? "pointer" : "not-allowed",
                color: saveFlash
                  ? "var(--accent-sky)"
                  : canSaveToLocker
                    ? "var(--text-muted)"
                    : "var(--text-muted)",
                opacity: canSaveToLocker ? 1 : 0.5,
                fontFamily: "Inter, sans-serif",
                fontSize: "var(--fs-label)",
                fontWeight: 600,
                letterSpacing: "1px",
                textTransform: "uppercase",
                transition: "color 0.2s, opacity 0.15s",
              }}
            >
              <Save
                aria-hidden
                style={{ width: 13, height: 13 }}
                strokeWidth={1.8}
              />
              Save to Captures
            </button>

            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              aria-label="Open XAES Analyst settings"
              className="panel-menu"
              style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
            >
              <Settings style={{ width: 14, height: 14 }} strokeWidth={1.8} aria-hidden />
            </button>
          </div>
        </div>

        {/* Status strip — legacy `.analyst-status` (line 628). */}
        <div
          className="flex flex-shrink-0 items-center gap-2"
          style={{
            padding: "6px 14px",
            borderBottom: "1px solid hsl(var(--border))",
          }}
        >
          <span
            className="font-mono"
            style={{
              fontSize: "var(--fs-base)",
              letterSpacing: "1.5px",
              color: "var(--accent-sky)",
            }}
          >
            XAES //
          </span>
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: display.dotColor,
              boxShadow: `0 0 6px ${display.dotColor}`,
              animation: display.pulse ? "pulse 2s infinite" : undefined,
            }}
          />
          <span
            className="font-mono uppercase"
            style={{
              fontSize: "var(--fs-label)",
              letterSpacing: "1.5px",
              color: "var(--text-muted)",
            }}
          >
            {display.label}
          </span>
        </div>

        {/* Body — messages + input. Legacy split them into
            `#chatMessages` (scrollable) + `.chat-input-area` at the
            bottom (lines 633-684). */}
        <div className="panel-body flex flex-col gap-2" style={{ padding: 10 }}>
          <ChatMessages
            messages={chat.messages}
            status={chat.status}
            error={chat.error}
            configured={chat.configured}
            onRetry={chat.retry}
          />

          <InsightSuggestions
            suggestions={insights.suggestions}
            scanning={insights.scanning}
            scope={workshopId}
            onAccept={handleAcceptInsight}
            onDismiss={insights.dismiss}
            onDismissAll={insights.dismissAll}
          />

          <div className="flex-shrink-0">
            <ChatInput
              scope={workshopId}
              status={chat.status}
              references={chat.contextReferences}
              onReferencesChange={chat.setContextReferences}
              onSend={(text, refs) => void chat.send(text, refs)}
              onAbort={chat.abort}
            />
          </div>
        </div>
      </div>

      <ClaudeSettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}

/**
 * Inner contents of the history dropdown — `+ New chat` row on top,
 * past sessions sorted newest-first below. Rendered as a separate
 * component so the relative-time `now` heartbeat doesn't re-render
 * the entire panel header.
 *
 * Visual chrome mirrors the `ReferencePicker` rows: 6 px vertical
 * padding on the list, 8×14 row padding, true-blue 0.06 hover wash.
 */
function HistoryList({
  sessions,
  sessionsHydrated,
  activeId,
  now,
  onNew,
  onPick,
  onDelete,
}: {
  sessions: ChatSession[];
  sessionsHydrated: boolean;
  activeId: string | null;
  now: number;
  onNew: () => void;
  onPick: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div
      className="max-h-72 overflow-y-auto"
      style={{
        padding: "6px 0",
        scrollbarWidth: "thin",
        scrollbarColor:
          "rgba(var(--accent-true-blue-rgb), 0.4) rgba(255, 255, 255, 0.03)",
      }}
    >
      <button
        type="button"
        onClick={onNew}
        className="w-full text-left transition-colors"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 14px",
          background: "transparent",
          border: 0,
          cursor: "pointer",
          fontFamily: "Inter, sans-serif",
          color: "var(--accent-true-blue)",
          fontSize: "var(--fs-base)",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            "rgba(var(--accent-true-blue-rgb), 0.06)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            "transparent";
        }}
      >
        <Plus
          aria-hidden
          style={{ width: 13, height: 13, flexShrink: 0 }}
          strokeWidth={2.5}
        />
        <span
          style={{
            flex: 1,
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          New chat
        </span>
      </button>

      {/* Subtle divider between the new-chat row and the history list.
          We don't use the shadcn separator so the colour can track the
          true-blue tint instead of `bg-muted`. */}
      {sessions.length > 0 && (
        <div
          aria-hidden
          style={{
            height: 1,
            margin: "4px 10px",
            background: "rgba(var(--accent-true-blue-rgb), 0.15)",
          }}
        />
      )}

      {!sessionsHydrated && sessions.length === 0 ? (
        <div
          style={{
            padding: "10px 14px",
            fontSize: "var(--fs-label)",
            fontWeight: 700,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            color: "var(--text-muted)",
          }}
        >
          Loading…
        </div>
      ) : sessions.length === 0 ? (
        <div
          style={{
            padding: "10px 14px",
            fontSize: "var(--fs-label)",
            fontWeight: 700,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            color: "var(--text-muted)",
          }}
        >
          No past chats
        </div>
      ) : (
        sessions.map((s) => (
          <HistoryRow
            key={s.id}
            session={s}
            active={s.id === activeId}
            now={now}
            onPick={() => onPick(s.id)}
            onDelete={() => onDelete(s.id)}
          />
        ))
      )}
    </div>
  );
}

/** Single history row — clicking the body loads the session, clicking
 *  the trash icon deletes it without bubbling the load click. */
function HistoryRow({
  session,
  active,
  now,
  onPick,
  onDelete,
}: {
  session: ChatSession;
  active: boolean;
  now: number;
  onPick: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const rowBg = active
    ? "rgba(var(--accent-true-blue-rgb), 0.10)"
    : hover
      ? "rgba(var(--accent-true-blue-rgb), 0.06)"
      : "transparent";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPick();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 14px",
        background: rowBg,
        border: 0,
        cursor: "pointer",
        fontFamily: "Inter, sans-serif",
        color: active ? "var(--accent-sky)" : "var(--text-secondary)",
        transition: "background 0.12s",
      }}
    >
      <MessageSquare
        aria-hidden
        style={{
          width: 13,
          height: 13,
          flexShrink: 0,
          color: active ? "var(--accent-sky)" : "var(--accent-true-blue)",
        }}
        strokeWidth={2}
      />
      <span
        style={{
          flex: 1,
          fontSize: "var(--fs-base)",
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {session.title || "New chat"}
      </span>
      <span
        style={{
          flexShrink: 0,
          fontSize: "var(--fs-label)",
          fontWeight: 600,
          letterSpacing: "0.5px",
          color: "var(--text-muted)",
        }}
      >
        {formatRelative(session.updatedAt, now)}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label={`Delete chat "${session.title || "New chat"}"`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
          background: "transparent",
          border: 0,
          borderRadius: 4,
          cursor: "pointer",
          color: "var(--text-muted)",
          opacity: hover || active ? 1 : 0.4,
          transition: "color 0.15s, opacity 0.15s",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.color =
            "var(--accent-red)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.color =
            "var(--text-muted)";
        }}
      >
        <Trash2
          aria-hidden
          style={{ width: 12, height: 12 }}
          strokeWidth={2}
        />
      </button>
    </div>
  );
}
